import type { ComponentPropsWithRef, HTMLAttributes, ReactNode } from 'react';

/** A register drawn as cards, in one of two layouts.
 *
 *  `grid` (the default) is one column in a narrow surface, two from 38rem and three from 58rem of the
 *  surface's OWN width. The wrapper is the measured container, so the grid answers to the space it was
 *  given rather than to the viewport or to whichever ancestor happens to be a container: the same register
 *  sits in surfaces of very different widths, and three cards across a phone is what the steps prevent.
 *  Three is the ceiling; a fourth column takes a card below the width its facts need.
 *
 *  `list` is one full-width card per row, for entries read as a line: a name and the sentence that says
 *  what it is for, which three narrow columns would break into a short stack each. */
export function CardGrid({ children, layout = 'grid', className = '', ...rest }: HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  layout?: 'grid' | 'list';
}) {
  const arrangement = layout === 'list'
    ? 'flex flex-col gap-2'
    : 'grid grid-cols-1 gap-3 @min-[38rem]:grid-cols-2 @min-[58rem]:grid-cols-3';
  return (
    <div className="@container min-w-0">
      <div role="list" data-layout={layout} className={`${arrangement} ${className}`} {...rest}>
        {children}
      </div>
    </div>
  );
}

/** One card of a `CardGrid`. The surface is not a button: a card carries its own controls, and a button
 *  around them is a control containing controls. A caller that makes the card open on click passes
 *  `onClick` for the pointer and still renders one real open control inside for the keyboard.
 *  `selected` paints the open entry on the surface (`data-selected`, for the styling) and names it on the
 *  `role="listitem"` (`aria-current`), so the open entry is marked in exactly one place rather than by
 *  every caller. `bulkSelected` reuses the same paint for a card ticked for a bulk action, without
 *  claiming `aria-current`: several ticked cards are not the open entry. `h-full` keeps a row of uneven
 *  cards the same height, so a caller's `mt-auto` footer lines up. The remaining props, `ref` and `style`
 *  included, land on the card surface itself rather than on the grid cell, so a caller that moves a card
 *  (a sortable register) moves exactly the painted box and the cell keeps its place in the grid. */
export function CardGridItem({ children, selected = false, bulkSelected = false, className = '', ...rest }: ComponentPropsWithRef<'div'> & {
  children: ReactNode;
  selected?: boolean;
  bulkSelected?: boolean;
}) {
  const clickable = rest.onClick !== undefined;
  const tinted = selected || bulkSelected;
  return (
    <div role="listitem" aria-current={selected ? 'true' : undefined} className="min-w-0">
      <div
        data-selected={tinted ? 'true' : undefined}
        className={`group flex h-full min-w-0 flex-col gap-3 rounded-xl border bg-card p-3.5 transition-colors ${clickable ? 'cursor-pointer' : ''} ${tinted ? 'border-primary/60 bg-selected-tint' : `border-border ${clickable ? 'hover:border-primary/40' : ''}`} ${className}`}
        {...rest}
        // After `rest`, so a caller's own data attributes never drop the marker base.css reads for titles.
        data-slot="card-grid-item"
      >
        {children}
      </div>
    </div>
  );
}
